import { action, observable, computed } from 'mobx';
import { RootStore } from 'stores/rootStore';
import {
  API_CLIENT_SIGN_DOCUMENTS_LIST,
  API_CLIENT_SIGN_REQUESTS_LIST,
  API_CLIENT_SIGN_PDF_FILE,
  API_CLIENT_SIGN_CONFIRM_DOCUMENT,
  API_CLIENT_SIGN_REQUEST_SMS,
  API_CLIENT_SIGN_VERIFY_SMS,
  API_CLIENT_SIGN_ABORT,
  API_CLIENT_SIGN_REQUEST,
  API_CLIENT_REQUEST_PDF_FILE,
  //API_CLIENT_SIGN_SETTINGS,
  API_CLIENT_SIGN_BUCKET,
} from 'api/endpoints';
import { SingleValueResponse } from 'common/interfaces';
import { AccountStatus } from 'enums/index';
//import { AccountSettings } from 'common/user';
import { APP_CONFIG } from 'appConfig';
import { Bucket, BucketType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import { scrollToTop } from 'helpers/helpers';

export interface PdfDocument {
  type: string;
  confirmed: boolean;
  confirmedOn?: string;
  loading?: boolean;
  id?: string;
}

interface Advisor {
  name: string;
  phone: string;
  email: string;
}

interface DocumentsData {
  documents: PdfDocument[];
  completed: boolean;
  change: boolean;
  advisor: Advisor;
  status: AccountStatus;
}

interface RequestsData {
  requests: PdfDocument[];
}

interface SettingsData {
  buckets: Bucket[];
  confirmed: BucketType[];
  options: { specialAssets: { savings: string[] } };
}

export enum SignatureFlowId {
  ALL_PORTFOLIOS_DOCS = 'ALL_PORTFOLIOS_DOCS',
  PORTFOLIO_ONLY_DOCS = 'PORTFOLIO_ONLY_DOCS',
  DOCS = 'DOCS',
}

export enum SignatureFlowStep {
  LONG_TERM = 'LONG_TERM',
  MID_TERM = 'MID_TERM',
  SHORT_TERM = 'SHORT_TERM',
  DOCS = 'DOCS',
}

export interface SignatureFlow {
  steps: { id: SignatureFlowStep; title: string; titleShort?: string; bucket?: boolean }[];
}

export const signatureFlows = {
  [SignatureFlowId.ALL_PORTFOLIOS_DOCS]: {
    steps: [
      {
        id: SignatureFlowStep.LONG_TERM,
        title: 'bucket.long_term.title',
        titleShort: 'bucket.long_term.title.short',
        bucket: true,
      },
      {
        id: SignatureFlowStep.MID_TERM,
        title: 'bucket.mid_term.title',
        titleShort: 'bucket.mid_term.title.short',
        bucket: true,
      },
      {
        id: SignatureFlowStep.SHORT_TERM,
        title: 'bucket.short_term.title',
        titleShort: 'bucket.short_term.title.short',
        bucket: true,
      },
      { id: SignatureFlowStep.DOCS, title: 'accountDetail_documents_client_title' },
    ],
  },
  [SignatureFlowId.PORTFOLIO_ONLY_DOCS]: {
    steps: [
      {
        id: SignatureFlowStep.LONG_TERM,
        title: 'bucket.long_term.title_portfolioOnly',
        titleshort: 'bucket.long_term.title_portfolioOnly.short',
        bucket: true,
      },
      { id: SignatureFlowStep.DOCS, title: 'accountDetail_documents_client_title' },
    ],
  },
  [SignatureFlowId.DOCS]: {
    steps: [{ id: SignatureFlowStep.DOCS, title: 'accountDetail_documents_client_title' }],
  },
};

export class SignatureStore {
  private rootStore: RootStore;
  private signOnly?: boolean;

  constructor(rootStore: RootStore, accountId: string, signOnly?: boolean) {
    this.rootStore = rootStore;
    this.accountId = accountId;
    this.signOnly = signOnly;
  }

  @observable accountId: string;
  @observable documents?: PdfDocument[];
  @observable requests?: PdfDocument[];
  @observable completed = false;
  @observable advisor?: Advisor = undefined;
  @observable wrongCode = false;
  @observable verifyDialog = false;
  @observable completeDialog: 'DOCS' | 'REQS' | 'SET' | null = null;
  @observable confirmedDocs: string[] = [];
  @observable change = false;
  @observable status?: AccountStatus = undefined;
  @observable signed = false;
  @observable canceled = false;
  @observable settings?: SettingsData = undefined;
  @observable buckets?: Bucket[] = undefined;
  @observable activeBucket = 0;
  @observable flow?: SignatureFlow = undefined;
  @observable flowStep?: SignatureFlowStep = undefined;
  @observable flowStepIndex = 0;

  setLoadingForDocument = (templateType: string, loading: boolean, request?: boolean) => {
    if (request && this.requests) {
      this.requests = this.requests.map((doc) => {
        if (doc.type === templateType) {
          doc.loading = loading;
        }
        return doc;
      });
    } else if (this.documents) {
      this.documents = this.documents.map((doc) => {
        if (doc.type === templateType) {
          doc.loading = loading;
        }
        return doc;
      });
    }
  };

  @action hideVeryficationDialog = () => {
    this.verifyDialog = false;
  };

  @action hideCompleteDialog = async () => {
    if (this.completeDialog === 'DOCS') {
      this.completeDialog = null;
      this.rootStore.stores.uiStore.setLoading(true);
      await this.rootStore.stores.userStore.fetchClientData();
      this.rootStore.stores.userStore.accountIdFromVs();
      this.rootStore.stores.uiStore.setLoading(false);
    } else {
      this.completeDialog = null;
    }
  };

  @action fetchAll = async (omitDocuments: boolean) => {
    await this.fetchRequests();
    if (!omitDocuments) await this.fetchDocuments();
    if (this.documents?.some((d) => d.type === 'INVESTMENT_PLAN')) {
      await this.fetchSettings(true);
    } else {
      this.flow = signatureFlows[SignatureFlowId.DOCS]; // FIXME
      this.flowStep = SignatureFlowStep.DOCS; // FIXME
      this.flowStepIndex = 0; // FIXME
    }
  };

  @action fetchDocuments = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<DocumentsData>(
      API_CLIENT_SIGN_DOCUMENTS_LIST(this.accountId),
    );
    if (response?.data) {
      this.documents = response.data.documents;
      this.completed = response.data.completed;
      this.confirmedDocs = this.documents.filter((doc) => doc.confirmed).map((doc) => doc.type);
      this.advisor = response.data.advisor;
      this.change = response.data.change;
      this.status = response.data.status;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action fetchRequests = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<RequestsData>(API_CLIENT_SIGN_REQUESTS_LIST);
    if (response?.data) {
      this.requests = response.data.requests;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action fetchSettings = async (initial: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.get<SettingsData>(API_CLIENT_SIGN_BUCKET(this.accountId));
    if (response?.data) {
      this.settings = response.data;
      if (this.settings?.buckets.every((b) => b.active)) {
        this.flow = signatureFlows[SignatureFlowId.ALL_PORTFOLIOS_DOCS];
      } else {
        this.flow = signatureFlows[SignatureFlowId.PORTFOLIO_ONLY_DOCS];
      }
      this.buckets = this.settings.buckets
        .slice()
        .reverse()
        .filter((b) => b.active);
      if (initial) {
        this.activeBucket = this.buckets.findIndex((b) => !this.settings!.confirmed.includes(b.type));
        if (this.activeBucket > -1) {
          this.flowStep = this.buckets[this.activeBucket].type as unknown as SignatureFlowStep;
          this.flowStepIndex = this.flow.steps.findIndex((s) => s.id === this.flowStep);
        } else {
          this.activeBucket = this.buckets.length - 1;
          this.flowStep = SignatureFlowStep.DOCS; // FIXME
          this.flowStepIndex = this.flow.steps.length - 1; // FIXME
        }
      }
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action confirmSettings = async (confirmType: string, hedging: boolean, next?: boolean) => {
    this.rootStore.stores.uiStore.setLoading(true);
    //const confirmed = this.settings?.confirmed.includes(this.buckets![this.activeBucket].type);
    const response = await this.rootStore.stores.apiStore.post<{ time: string }>(
      API_CLIENT_SIGN_BUCKET(this.accountId),
      { type: confirmType, currencyHedging: hedging },
    );
    if (response?.data) {
      await this.fetchSettings(false);
      if (next) this.goFwd();
    } else {
      this.rootStore.stores.errorStore.showErrorDialog('documents_sign_error_confirmBucket');
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action goFwd = () => {
    if (this.buckets && this.flow!.steps[this.flowStepIndex + 1].bucket) {
      this.activeBucket++;
      scrollToTop();
    }
    this.flowStepIndex++;
    this.flowStep = this.flow!.steps[this.flowStepIndex].id;
  };

  @action goBack = () => {
    if (
      this.buckets &&
      this.flow!.steps[this.flowStepIndex].bucket &&
      this.flow!.steps[this.flowStepIndex - 1].bucket
    ) {
      this.activeBucket--;
      scrollToTop();
    }
    this.flowStepIndex--;
    this.flowStep = this.flow!.steps[this.flowStepIndex].id;
  };

  @action confirmDocument = async (confirmType: string) => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.post<{ time: string }>(
      API_CLIENT_SIGN_CONFIRM_DOCUMENT(this.accountId, confirmType, this.change ? 'true' : 'false'),
    );
    if (response?.data) {
      this.fetchDocuments();
    } else {
      this.rootStore.stores.errorStore.showErrorDialog('documents_sign_error_confirmDocument');
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action downloadDocument = async (templateType: string, request?: boolean, requestId?: string) => {
    this.setLoadingForDocument(templateType, true, request);
    const response = request
      ? await this.rootStore.stores.apiStore.get<any>(API_CLIENT_REQUEST_PDF_FILE(requestId))
      : await this.rootStore.stores.apiStore.get<any>(
          API_CLIENT_SIGN_PDF_FILE(this.accountId, templateType, this.change ? 'true' : 'false'),
        );
    if (!response || response.status !== 200) {
      this.rootStore.stores.errorStore.showErrorDialog();
    }
    this.setLoadingForDocument(templateType, false, request);
  };

  @action requestCode = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    this.wrongCode = false;
    const response = await this.rootStore.stores.apiStore.post<{ time: string }>(
      API_CLIENT_SIGN_REQUEST_SMS(this.accountId),
    );
    if (!response || response.status !== 200) {
      this.rootStore.stores.errorStore.showErrorDialog('documents_sign_error_generatingCode');
    } else {
      this.verifyDialog = true;
    }
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action abortSigning = async () => {
    this.rootStore.stores.uiStore.setLoading(true);
    const response = await this.rootStore.stores.apiStore.delete<{ time: string }>(
      API_CLIENT_SIGN_ABORT(this.accountId),
    );
    this.rootStore.stores.uiStore.setLoading(false);
    if (response?.data?.time) {
      if (this.signOnly) {
        this.canceled = true;
        this.completeAction();
      } else {
        this.canceled = true;
        this.rootStore.stores.uiStore.setLoading(true);
        await this.rootStore.stores.userStore.fetchClientData();
        this.rootStore.stores.userStore.accountIdFromVs();
        this.rootStore.stores.uiStore.setLoading(false);
      }
    } else {
      this.rootStore.stores.errorStore.showErrorDialog();
    }
  };

  @action verifyCode = async (code: string) => {
    this.rootStore.stores.uiStore.setLoading(true);

    this.wrongCode = false;

    const response = await this.rootStore.stores.apiStore.post<{ time: string }>(
      API_CLIENT_SIGN_VERIFY_SMS(this.accountId),
      { code },
    );

    if (!response || response.status !== 200) {
      switch (response?.status) {
        case 412:
          this.wrongCode = true;
          break;
        case 409:
          this.hideVeryficationDialog();
          this.rootStore.stores.errorStore.showErrorDialog('documents_sign_attemptsExceeded');
          break;
        default:
          this.rootStore.stores.errorStore.showErrorDialog('documents_sign_error_verify');
          break;
      }
    } else {
      this.hideVeryficationDialog();
      this.signed = true;
      this.completeDialog = 'DOCS';
    }

    this.rootStore.stores.uiStore.setLoading(false);
  };

  @action confirmRequest = async (requestId: string, action: 'CONFIRM' | 'CANCEL') => {
    this.rootStore.stores.uiStore.setLoading(true);

    const response =
      action === 'CONFIRM'
        ? await this.rootStore.stores.apiStore.post<{ time: string }>(API_CLIENT_SIGN_REQUEST(requestId))
        : await this.rootStore.stores.apiStore.delete<SingleValueResponse<boolean>>(API_CLIENT_SIGN_REQUEST(requestId));
    if (response?.data) {
      await this.fetchRequests();
    } else {
      this.rootStore.stores.errorStore.showErrorDialog('documents_sign_error_confirmDocument');
    }

    if (this.requests?.length === 0) this.completeDialog = 'REQS';
    this.rootStore.stores.uiStore.setLoading(false);
  };

  @computed
  get incomplete(): boolean {
    return (
      !this.completed ||
      this.signed ||
      (!!this.requests && this.requests.length > 0) ||
      (!!this.settings && !!this.buckets && this.buckets.length < this.settings.confirmed.length)
    );
  }

  nothingLeft = (): boolean => {
    return (
      (this.completeDialog === 'DOCS' && this.requests?.length === 0) ||
      (this.completeDialog === 'REQS' && (this.signed || this.canceled || this.documents?.length === 0))
    );
  };

  completeAction = () => {
    if (
      this.signOnly &&
      this.requests?.length === 0 &&
      (this.signed || this.canceled || this.documents?.length === this.confirmedDocs.length)
    )
      this.rootStore.stores.userStore.logout(APP_CONFIG.extWebsite);
  };
}
